export default {
  'deductionToInduction': {
    'course-title': 'Transforming Deduction to Induction',
    'course-name': 'Section 3B-4 <span class="unit-title">Transforming Deduction<br/>to Induction<span>',
    'course-video': {
      'title': 'Section 3 SUPPORTING THE POINTS<br/>1’04.16 – 1’12:55 TRANSFORMING DEDUCTION TO INDUCTION -:<br/>3C Transforming deduction to induction',
      'src': 'https://www.youtube.com/embed/5aov7rXM3Vw?enablejsapi=1',
      'lecture-title': '3B-4 Lecture'
    },
    'unit-section-title': '3B-4 Exercises',
    'exercise-intro': `
    <p>This structure presents the author’s thinking as it came to him, rather than attempting to communicate the heart of the message. But as you can see, the same subjects are discussed in all three sections leading to the “steps,” which are really the main points of the document. Once you see that, it is relatively easy to build your structure inductively around them, like this, with the information that supports each section underneath.</p>
    <br/>
    <img class="intro-img2" src="/assets/images/structure-3b4-2.png" alt="Original structure" />
    <br/>
    <p>Following are five opportunities for you to restructure a pyramid from deductive to inductive.</p>`,
    'deductive-form': '<u>Deductive form</u>',
    'exercise-bottom': `<p>In each case I have given you the original deductive structure, a blank pyramid for restructuring the ideas inductively, and a list of the inductive ideas to place properly in the pyramid.</p>`,
    'menu-list': {
      'exercise-1': 'Policy Uncertainty',
      'exercise-2': 'Mind Tapes',
      'exercise-3': 'Can you Teach an Old Dog New Tricks?',
      'exercise-4': 'Euro Illusions',
      'exercise-5': 'Banking on the State: the Philosopher’s Stone of Finance'
    },
    'exercise-1': {
      'number': '3B-4 - Exercise 1',
      'title': 'Policy Uncertainty',
      'description': `Policy uncertainty is bad for business. A large body of research shows that when overall uncertainty increases, GDP falls. Companies invest less, trade less, and hire fewer people. Consumers spend less.
      <br/><br/>
      The effects are even more insidious than they seem at first. <span class="js-ex1" ng-class="{'highlight-word': vm.highlightWords.ex1}">Uncertainty paralyzes, making businesses and consumers less sensitive to outside forces.</span> In a high-uncertainty environment, government policies to reverse a recession aren’t very effective – as they so notably weren’t in the last recession. And <span class="js-ex1" ng-class="{'highlight-word': vm.highlightWords.ex1}">because of the paralyzing effects, productive firms are slow to expand and unproductive firms are slow to contract</span> – so <span class="js-ex1" ng-class="{'highlight-word': vm.highlightWords.ex1}">resources don’t get reallocated from the less productive to the more productive,</span> shutting down the primary mechanism for increasing productivity.`,
      'reference': `You can be sure that policy uncertainty is here to say<br/>by Geoff Colvin, writing in Forbes Magazine 2014`,
    },
    'exercise-2': {
      'number': '3B-4 - Exercise 2',
      'title': 'Mind Tapes',
      'description': `Fascinating as the idea of mind tapes is, the supposition that such personality preservation might someday be possible is almost certainly wrong. The fundamental obstacle is that brains are not like computers, fresh from the factory and all alike. Even at birth human <span class="js-ex2" ng-class="{'highlight-word': vm.highlightWords.ex2}">brains are no doubt uniquely configured</span> – like fingerprints - and a lifetime of learning and experience can only enhance their idiosyncrasies. There are scant grounds then for
hope that anything with the <span class="js-ex2" ng-class="{'highlight-word': vm.highlightWords.ex2}">hardware-independence of a program can be “read out” of a brain</span> (at a periodic “mind taping” session). There is even less hope that such a mind tape, even if it could be construed, <span class="js-ex2" ng-class="{'highlight-word': vm.highlightWords.ex2}">would be compatible with another brain’s hardware.</span> Computers are designed to <span class="js-ex2" ng-class="{'highlight-word': vm.highlightWords.ex2}">be readily redesignable (at another level) by the insertion</span>, in one big lump or rush, of a new program; brains presumably are not.`,
'reference': `The Mind’s Eye: Fantasies and Reflections on Self and Soul<br/>
by Douglas R. Hofstadter and Daniel C. Dennett, Basic Books 2000`,
      'diagram': `
        <p style="margin-left: -233px;" class="diagram-question">How do you know?</p>
        <div class="diagram-box">
          <p>The idea of mind taping to preserve personality is almost certainly wrong</p>
        </div>
        <div class="connector">
          <svg class="svg-long-deductive-arrow">
            <line x1="5" y1="5" x2="377" y2="117" />
            <path d="M 0 0 L 13 15 L 19 0 Z" />
          </svg>
        </div>
        <div class="diagram-slots">
          <div class="diagram-box">
            <p>Brains are uniquely configured</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div class="diagram-box">
            <p>Don’t have a tape that could record the uniqueness</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div class="diagram-box">
            <p>Couldn’t find hardware on which to read the recording</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div class="diagram-box">
            <p>Can’t insert the results into another brain</p>
          </div>
        </div>`
    },
    'exercise-3': {
      'number': '3B-4 - Exercise 3',
      'title': 'Can You Teach an Old Dog New Tricks?',
      'description': `<span class="js-ex3" ng-class="{'highlight-word': vm.highlightWords.ex3}">After adolescence, our neural network brains are never again quite so flexible as when we were children.</span> Does this mean that it’s impossible to learn something new – like psycotherapic change - while unlearning something old?
      <br/><br/>There is a widespread <span class="js-ex3" ng-class="{'highlight-word': vm.highlightWords.ex3}">impression, usually tacit, that “change” means getting rid wholly of old patterns and replacing them wholly with new ones.</span> The operation of neural networks in the brain sheds light on this mistaken notion. In brief, you certainly can teach an old dog new tricks, but it is more difficult to do so, depending on what his old tricks are, and whether he is a dog willing to pick himself up and start all over again, and again, or not.
      <br/><br/>Psychotherapeutic change is fundamentally little different from learning, chiefly because <span class="js-ex3" ng-class="{'highlight-word': vm.highlightWords.ex3}">it shares with learning the same kind of obstacles:  laziness, the quest for novelty, the desire for instant gratification, and especially the avoidance of failure.</span> We don’t want to remember the famous men who had to fall to rise again, because it suggests that to meet our own challenges we might need to do the same.
      <br/><br/>The <span class="js-ex3" ng-class="{'highlight-word': vm.highlightWords.ex3}">solution to the old-dog problem is practical and pedestrian: repetition, perseverance, courage, willingness to start all over again.</span> New patterns may be embedded in a fresh network relatively quickly. When a network is already converged to a specific set of patterns, it will require many more repetitions to “overlearn” a new – especially a contradictory – pattern.`,
      'reference': `The Quantum Brain:<br/>The Search for Freedom and the Next Generation of Man<br/>by Jeffrey Satinover, John Wiley & Sons 2001`,
      'diagram': `
        <p style="margin-top: -178px;" class="diagram-question">Why?</p>
        <div class="diagram-box">
          <p>You can certainly teach an old dog new tricks, but it is more difficult</p>
        </div>
        <div class="connector">
          <svg class="svg-long-deductive-arrow">
            <line x1="5" y1="5" x2="377" y2="117" />
            <path d="M 0 0 L 13 15 L 19 0 Z" />
          </svg>
        </div>
        <div class="diagram-slots">
          <div class="diagram-box">
            <p>Post-adolescent brains are never quite so flexible as when we were children</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div class="diagram-box">
            <p>Belief is that “change” requires getting rid of old patterns before adding new ones</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div class="diagram-box">
            <p>Actually it only requires overcoming normal obstacles to learning (laziness, etc.)</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div class="diagram-box">
            <p>The solution requires repetition perseverence, and courage</p>
          </div>
        </div>
        <div class="connector">
         <svg>
          <line x1="679" y1="0" x2="250" y2="115" class="connector__line"/>
          <line x1="679" y1="0" x2="460" y2="115" class="connector__line"/>
          <line x1="675" y1="0" x2="675" y2="115" class="connector__line"/>
        </svg>
        </div>
        <div style="justify-content: flex-end; width: 100%;" class="diagram-slots">
          <div class="diagram-box">
            <p>A willingness to start</p>
          </div>
          <div style="margin: 0 8px;" class="diagram-box">
            <p>A willingness to work at it</p>
          </div>
          <div class="diagram-box">
            <p>A willingness to keep going no matter what</p>
          </div>
        </div>`
    },
    'exercise-4': {
      'number': '3B-4 - Exercise 4',
      'title': 'Euro Illusions',
      'description': `There are contradictions built into Europe’s economic system. In a single-currency system, policymakers lack the most powerful tool for helping individual economies adjust to setbacks – i.e., <span class="js-ex4" ng-class="{'highlight-word': vm.highlightWords.ex4}">interest rates set according to national conditions.</span> To succeed, a single-currency system needs <span class="js-ex4" ng-class="{'highlight-word': vm.highlightWords.ex4}">either large fiscal transfers</span> (so fiscal policy can do what monetary policy can’t) <span class="js-ex4" ng-class="{'highlight-word': vm.highlightWords.ex4}">or highly integrated labor</span> markets (so the unemployed can move to stronger
      markets to find work), and preferably both. The <span class="js-ex4" ng-class="{'highlight-word': vm.highlightWords.ex4}">euro area has neither,</span> and its governments even after an epic sovereign debt crisis, have no plans to do much about it. This leaves the EU’s weakest economies <span class="js-ex4" ng-class="{'highlight-word': vm.highlightWords.ex4}">with no choice but to restore their prospects through the brutality of “internal devaluation”</span> – using high unemployment to force down labor costs. `,
      'reference': `Euro Illusions<br/>by Clive Crook, Bloomberg Business Week, May 2, 2014`,
      'note': `<p><u>Note</u></p><p>Fiscal policy - Increase/decrease taxes and spending to influence the economy.<br/>
      Monetary policy - Raise/lower interest rates to control the money supply.</p>`,
      'diagram': `
        <p style="margin-left: -233px; margin-top: -100px;" class="diagram-question">How do you know?</p>
        <div class="diagram-box">
          <p>Weaker economies inevitably suffer an epic sovereign debt crisis in a single currency market</p>
        </div>
        <div class="connector">
          <svg class="svg-long-deductive-arrow">
            <line x1="5" y1="5" x2="377" y2="117" />
            <path d="M 0 0 L 13 15 L 19 0 Z" />
          </svg>
        </div>
        <div class="diagram-slots">
          <div style="height: 150px" class="diagram-box">
            <p>In a single currency system, policy makers lack the ability to set interest rates according to national condition</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div style="height: 150px" class="diagram-box">
            <p>To succeed, the system needs either large fiscal transfers (to add to the money supply) or integrated labour markets (to provide work for the unemployed)</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div style="height: 150px" class="diagram-box">
            <p>The Euro area has neither tool, nor do its governments plan to do much about it</p>
          </div>
          <svg class="svg-deductive-arrow">
            <line x1="50" y1="25" x2="0" y2="25" />
            <path d="M 50 25 L 35 18 L 39 25 L 35 32 Z" />
          </svg>
          <div style="height: 150px" class="diagram-box">
            <p>Leaves EU’s weakest economies with no choice but internal devaluation - using high unemployment to force down labour costs</p>
          </div>
        </div>`
    },
    'exercise-5': {
      'number': '3B-4 - Exercise 5',
      'title': 'Banking on the State:<br/>The Philosopher’s Stone of Finance',
      'description': `By 1694 William of Orange had been on the English throne for 5 years, and his credit with potential lenders remained abysmal. But the late seventeenth century in England was an age of vigorous innovation in the fields of money and public finance. One proposal put forward was a plan for a bank that would fundamentally reorganize the funding of the king’s debts. Investors would subscribe capital to the bank, and the bank would lend money to the government. <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">This Bank of England would in effect be a public-private partnership.</span> The <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">Bank’s primary role would undoubtedly be to put the Sovereign’s credit and finances on a surer footing.</span> Indeed, its design, governance, and management were to be delegated to the mercantile classes precisely in order to ensure confidence in its operations and credit control. But in return the Sovereign was to grant important privileges. Above all, <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">the Bank was to enjoy the right to issue banknotes</span> – a license to put into circulation paper currency representing its own liabilities, which could circulate as money. There was to be, quite literally, a quid pro quo.
      <br/><br/>If they and the private money interest they represented would agree to fund the king on terms over which they, as the Directors of the new Bank, would have a statutory say, then the king would in turn allow them a statutory share in his most ancient and jealously guarded prerogative: the creation of money and the management of its standard. To be granted the privilege of note issue by the crown, which would anoint the liabilities of a private bank with the authority of the Sovereign, was the Philosopher’s Stone of money. It was the endorsement that could
      liberate private bank money from its parochial bounds. They would lend their credit to the Sovereign – he would lend his authority to their bank. What they would sow by agreeing to lend, they would reap a hundredfold in being allowed to create private money with the Sovereign’s endorsement. Henceforth, the seigniorage would be shared.
      <br/><br/>There were benefits to both parties. <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">The quid pro quo nourished a virtuous circle.</span> The state’s blessing afforded <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">general circulation to the Bank’s notes.</span> The commercial ownership and management of the bank <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">improved the state’s creditworthiness.</span> Adam Smith delivered his verdict in 1776. “The stability of the Bank of England is equal to that of the British government.” Without the state, the Bank would have lacked authority; without the Bank, the state would have lacked credit.
      <br/><br/>This compromise is the direct ancestor of the monetary systems that dominate the world today: <span class="js-ex5" ng-class="{'highlight-word': vm.highlightWords.ex5}">systems in which the creation and management of money are almost entirely delegated to private banks, but in which sovereign money remains the “final settlement asset”;</span> the only credit balance with which the banks on the penultimate tier of the pyramid can be certain of settling payments to one another or to the state. Likewise, cash remains strictly a token of a credit held against the sovereign, but the overwhelming majority of the money in circulation consists of credit balances on accounts at private banks. The fusion of Sovereign and private money born of the political compromise struck in 1694 remains the bedrock of the modern monetary world.`,
      'reference': `Money: The Unauthorised Biography<br/>by Felix Martin, Bodley Head 2013`,
      'note': '<p><u>Note</u></p><p>On this one, I give you the structure of each paragraph, show you how the parts form the entire deductive argument, restructure it to be inductive, and ask you to fill in only the Key Line points of the new structure.</p>',
      'diagram': `
        <p style="margin-left: -233px; margin-top: -190px;" class="diagram-question">How know?</p>
        <div style="width: 270px" class="diagram-box">
          <p>Creation of the Bank of England in 1694 was the result of a compromise between the King and his bankers that is still the basis of modern banking</p>
        </div>
        <div class="connector">
          <svg class="svg-long-deductive-arrow">
            <line x1="5" y1="5" x2="377" y2="117" />
            <path d="M 0 0 L 13 15 L 19 0 Z" />
          </svg>
        </div>
        <div class="diagram-slots" style="width: 930px; justify-content: center">
            <div style="height: 115px;" class="diagram-box">
               <p>Bank of England was created as a public/private partnership between the King and his bankers</p>
            </div>
            <svg class="svg-deductive-arrow">
              <line x1="157" y1="25" x2="0" y2="25" />
              <path d="M 157 25 L 142 18 L 146 25 L 142 32 Z" />
            </svg>
            <div style="height: 115px;" class="diagram-box">
              <p>Brought great benefits to both parties</p>
            </div>
            <svg class="svg-deductive-arrow">
              <line x1="157" y1="25" x2="0" y2="25" />
              <path d="M 157 25 L 142 18 L 146 25 L 142 32 Z" />
            </svg>
            <div style="height: 115px;" class="diagram-box">
              <p>Was picked up worldwide as a good idea</p>
            </div>
        </div>
        <svg class="svg">
            <line x1="115" y1="0" x2="80" y2="110" />
            <line x1="115" y1="0" x2="250" y2="110" />
            <line x1="470" y1="0" x2="390" y2="110" />
            <line x1="470" y1="0" x2="560" y2="110" />
            <line x1="840" y1="0" x2="690" y2="110" />
            <line x1="840" y1="0" x2="860" y2="110" />
        </svg>
        <div class="diagram-slots six-slots" style="width: 930px;">
          <div class="diagram-box">
            <p>King got access to credit and a stable currency</p>
          </div>
          <div class="diagram-box">
            <p>Bankers got right to issue banknotes</p>
          </div>
          <div class="diagram-box">
            <p>Bank’s notes circulated generally</p>
          </div>
          <div class="diagram-box">
            <p>State got improved credit-worthiness</p>
          </div>
          <div class="diagram-box">
            <p>Cash remains strictly a token of a credit held against the State</p>
          </div>
          <div class="diagram-box">
            <p>Money in circulation consists of credit balances on accounts in private banks</p>
          </div>
        </div>`
    },
    'congratulation-message': 'THANK YOU',
    'finish-section': 'You have completed Section 3B',
    'final-description': 'You know the technique for shifting a deductive argument to inductive form',
    'next-course-button': 'proceed to section 3C',
    'dashboard-button': 'Main menu',
  }
}



// WEBPACK FOOTER //
// ./client/app/modules/courses/3/3b4/deductionToInduction/i18n/en.js